"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from "react";
import MessageList from "@/app/[tenant]/(console)/comms/chat/window/message/message-list";
import { cx } from "@/lib/tailwind-utils";
import { useChatConfigContext } from "./chat-context";
import { ChatEmpty } from "./chat-empty";
import { matchesChatFilter } from "./chat-filter";
import { ChatLoading } from "./chat-loading";
import { useChatFilterContext, useChatScrollRefContext } from "./contexts";
import { useChatData, useChatMessages } from "./hooks";
import { useChatScroll } from "./hooks/use-chat-scroll";
import { useSuggestionDisplay } from "./hooks/use-suggestion-display";
export function ChatMessages() {
  const $ = _c(50);
  const {
    chatId,
    size,
    showSuggestions
  } = useChatConfigContext();
  let t0;
  if ($[0] !== chatId) {
    t0 = {
      chatId
    };
    $[0] = chatId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    messages,
    emails,
    isLoading
  } = useChatData(t0);
  const {
    chatMessagesRef
  } = useChatScrollRefContext();
  const {
    channels,
    searchQuery,
    isFiltered,
    clearFilters
  } = useChatFilterContext();
  let t1;
  if ($[2] !== messages) {
    t1 = {
      messages
    };
    $[2] = messages;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    lastMessage,
    lastPatientMessage,
    displaySuggestion
  } = useChatMessages(t1);
  let t2;
  bb0: {
    if (!isFiltered) {
      t2 = messages.length;
      break bb0;
    }
    let t3;
    if ($[4] !== channels || $[5] !== emails || $[6] !== messages || $[7] !== searchQuery) {
      t3 = [...messages, ...emails].filter(item => matchesChatFilter(item, channels, searchQuery));
      $[4] = channels;
      $[5] = emails;
      $[6] = messages;
      $[7] = searchQuery;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = t3.length;
  }
  const shownMessagesLength = t2;
  let t3;
  if ($[9] !== chatId || $[10] !== isLoading || $[11] !== shownMessagesLength) {
    t3 = {
      chatId,
      messagesLength: shownMessagesLength,
      isLoading
    };
    $[9] = chatId;
    $[10] = isLoading;
    $[11] = shownMessagesLength;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const {
    chatContainerRef,
    scrollToBottom
  } = useChatScroll(t3);
  let t4;
  if ($[13] !== displaySuggestion || $[14] !== lastPatientMessage || $[15] !== messages) {
    t4 = {
      messages,
      lastPatientMessage,
      displaySuggestion
    };
    $[13] = displaySuggestion;
    $[14] = lastPatientMessage;
    $[15] = messages;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const {
    suggestion,
    setSuggestion
  } = useSuggestionDisplay(t4);
  const shouldShowSuggestion = showSuggestions && suggestion !== undefined;
  let t5;
  if ($[17] !== channels || $[18] !== searchQuery) {
    t5 = {
      channels,
      searchQuery
    };
    $[17] = channels;
    $[18] = searchQuery;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const previousFilterRef = useRef(t5);
  let t6;
  let t7;
  if ($[20] !== channels || $[21] !== scrollToBottom || $[22] !== searchQuery) {
    t6 = () => {
      const previous = previousFilterRef.current;
      if (previous.channels !== channels || previous.searchQuery !== searchQuery) {
        previousFilterRef.current = {
          channels,
          searchQuery
        };
        scrollToBottom();
      }
    };
    t7 = [channels, searchQuery, scrollToBottom];
    $[20] = channels;
    $[21] = scrollToBottom;
    $[22] = searchQuery;
    $[23] = t6;
    $[24] = t7;
  } else {
    t6 = $[23];
    t7 = $[24];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[25] !== chatMessagesRef || $[26] !== scrollToBottom) {
    t8 = () => {
      chatMessagesRef.current = {
        scrollToBottom
      };
      return () => {
        chatMessagesRef.current = null;
      };
    };
    t9 = [chatMessagesRef, scrollToBottom];
    $[25] = chatMessagesRef;
    $[26] = scrollToBottom;
    $[27] = t8;
    $[28] = t9;
  } else {
    t8 = $[27];
    t9 = $[28];
  }
  useEffect(t8, t9);
  if (isLoading) {
    let t10;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <ChatLoading />;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    return t10;
  }
  if (messages.length === 0 && emails.length === 0) {
    let t10;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <ChatEmpty />;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    return t10;
  }
  const t10 = isFiltered && size === "lg" && "md:pt-32";
  let t11;
  if ($[31] !== t10) {
    t11 = cx("h-0 flex-1 overflow-y-auto px-2 pt-44 pb-56 motion-opacity-in-0 md:pt-24 md:pb-36", t10);
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== clearFilters || $[34] !== scrollToBottom) {
    t12 = () => {
      clearFilters();
      scrollToBottom(true);
    };
    $[33] = clearFilters;
    $[34] = scrollToBottom;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== emails || $[37] !== lastMessage || $[38] !== lastPatientMessage || $[39] !== messages || $[40] !== setSuggestion || $[41] !== shouldShowSuggestion || $[42] !== size || $[43] !== suggestion || $[44] !== t12) {
    t13 = <MessageList messages={messages} emails={emails} lastMessage={lastMessage} lastPatientMessage={lastPatientMessage} suggestion={suggestion} setSuggestion={setSuggestion} onSendMessage={t12} showSuggestion={shouldShowSuggestion} size={size} />;
    $[36] = emails;
    $[37] = lastMessage;
    $[38] = lastPatientMessage;
    $[39] = messages;
    $[40] = setSuggestion;
    $[41] = shouldShowSuggestion;
    $[42] = size;
    $[43] = suggestion;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== chatContainerRef || $[47] !== t11 || $[48] !== t13) {
    t14 = <div className={t11} ref={chatContainerRef}>{t13}</div>;
    $[46] = chatContainerRef;
    $[47] = t11;
    $[48] = t13;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  return t14;
}